import type { ReactNode } from "react";

import { useLayoutEffect, useRef, useState } from "react";
import { ToggleButton, ToggleButtonGroup } from "react-aria-components";
import { twMerge } from "tailwind-merge";

export interface SegmentedControlOption<T extends string> {
  id: T;
  label: ReactNode;
}

export function SegmentedControl<T extends string>({
  "aria-label": ariaLabel,
  className,
  onChange,
  options,
  value,
}: {
  "aria-label": string;
  className?: string;
  onChange: (value: T) => void;
  options: SegmentedControlOption<T>[];
  value: T;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef(new Map<T, HTMLButtonElement>());
  const [indicator, setIndicator] = useState<{ left: number; width: number }>();

  // The indicator is measured rather than sized in CSS so it can slide between options of different widths.
  useLayoutEffect(() => {
    const container = containerRef.current;
    const selected = optionRefs.current.get(value);

    if (!container || !selected) return;

    const measure = () =>
      setIndicator({ left: selected.offsetLeft, width: selected.offsetWidth });

    measure();
    const observer = new ResizeObserver(measure);

    observer.observe(container);

    return () => observer.disconnect();
  }, [value]);

  return (
    <ToggleButtonGroup
      aria-label={ariaLabel}
      className={twMerge(
        "relative inline-flex rounded-full bg-bg-tertiary p-xs",
        className,
      )}
      disallowEmptySelection
      onSelectionChange={(keys) => {
        const [selected] = keys;

        if (selected !== undefined) onChange(selected as T);
      }}
      ref={containerRef}
      selectedKeys={[value]}
      selectionMode="single"
    >
      {indicator && (
        <span
          aria-hidden
          className="absolute inset-y-xs left-0 rounded-md bg-bg-primary shadow-sm transition-[transform,width] duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] motion-reduce:transition-none"
          style={{
            transform: `translateX(${indicator.left}px)`,
            width: indicator.width,
          }}
        />
      )}
      {options.map((option) => (
        <ToggleButton
          className="relative z-10 cursor-pointer rounded-md px-lg py-sm text-sm font-semibold text-text-tertiary outline-hidden transition-colors duration-300 hover:text-text-secondary focus-visible:ring-2 focus-visible:ring-border-brand data-selected:text-text-secondary motion-reduce:transition-none"
          id={option.id}
          key={option.id}
          ref={(element) => {
            if (element) optionRefs.current.set(option.id, element);
            else optionRefs.current.delete(option.id);
          }}
        >
          {option.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}
